import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  Optional,
  Self,
  ViewChild,
  ElementRef
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  ReactiveFormsModule
} from '@angular/forms';

@Component({
  selector: 'app-input-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div class="relative">
      <input
        #inputRef
        [type]="nativeType"
        [id]="id"
        [name]="name"
        [attr.autocomplete]="autocomplete || 'off'"
        [placeholder]="placeholder ?? ''"
        [value]="value"
        [min]="min ?? ''"
        [max]="max ?? ''"
        [step]="step ?? 1"
        [disabled]="disabled"
        [ngClass]="inputClasses"
        (input)="onInput($event)"
        (keydown)="onKeydown($event)"
        (blur)="onTouched()"
      />

      @if (hint) {
        <p class="mt-1.5 text-xs"
          [ngClass]="{
            'text-error-500': error,
            'text-success-500': success,
            'text-gray-500': !error && !success
          }">
          {{ hint }}
        </p>
      }
    </div>
  `,
})
export class InputFieldComponent implements ControlValueAccessor {

  @Input() type: string = 'text';
  @Input() id?: string = '';
  @Input() name?: string = '';
  @Input() autocomplete?: string = '';
  @Input() placeholder?: string = '';
  @Input() value: string | number = '';
  @Input() min?: string;
  @Input() max?: string;
  @Input() step?: number;
  @Input() disabled: boolean = false;
  @Input() success: boolean = false;
  @Input() error: boolean = false;
  @Input() hint?: string;
  @Input() className: string = '';
  @ViewChild('inputRef', { static: true }) inputRef!: ElementRef<HTMLInputElement>;
  @Input() hasRightIcon: boolean = false;
  @Input() blockSpaces: boolean = false;

  /** @deprecated Use reactive form binding instead */
  @Output() valueChange = new EventEmitter<string | number>();

  // ControlValueAccessor callbacks
  private onChange: (val: string | number) => void = () => { };
  onTouched: () => void = () => { };

  constructor(@Optional() @Self() private ngControl: NgControl) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  get nativeType(): string {
    if (this.autocomplete === 'new-password') {
      return 'password';
    }
    return this.type;
  }

  // ---------- ControlValueAccessor ----------

  writeValue(val: string | number): void {
    this.value = val ?? '';
  }

  registerOnChange(fn: (val: string | number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ------------------------------------------

  get inputClasses(): string {
    let base = `h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 ${this.className}`;

    if (this.autocomplete === 'new-password' && this.type === 'text') {
      base += ' [text-security:disc] [-webkit-text-security:disc]';
    }

    if (this.hasRightIcon) {
      base += ' pr-8';
    }

    if (this.disabled) {
      base += ' text-gray-500 border-gray-300 opacity-40 bg-gray-100 cursor-not-allowed';
    } else if (this.error) {
      base += ' border-error-500 focus:border-error-300 focus:ring-error-500/20';
    } else if (this.success) {
      base += ' border-success-500 focus:border-success-300 focus:ring-success-500/20';
    } else {
      base += ' bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20';
    }

    return base;
  }

  onKeydown(event: KeyboardEvent) {
    if (this.blockSpaces && event.key === ' ') {
      event.preventDefault();
    }
  }

  onInput(event: Event) {
    const input = event.target as HTMLInputElement;

    if (this.blockSpaces && /\s/.test(input.value)) {
      input.value = input.value.replace(/\s/g, '');
    }

    const val = this.type === 'number' ? +input.value : input.value;
    this.value = val;
    this.onChange(val);
    this.valueChange.emit(val);
  }
}
